import React from "react";

/**
 * Desktop Hero — independent layout, no responsive stretching from mobile.
 * Visible only at >= 1024px (lg breakpoint).
 * The <section> itself is transparent — the App wrapper carries the background.
 */
export default function HeroDesktop() {
  const thumbStyleBase = {
    aspectRatio: "16/9",
    filter: "blur(2.5px)",
    boxShadow: "0 24px 48px rgba(0,0,0,0.55)",
  };

  return (
    <section className="hidden lg:block relative w-full overflow-hidden" style={{ paddingTop: "88px" }}>
      <div
        className="relative w-full mx-auto"
        style={{ aspectRatio: "1920/830", maxWidth: "1920px" }}
      >
        {/* Container shape (dark panel with concave bottom notch + purple glow) */}
        <div
          className="absolute"
          style={{
            left: "6%",
            top: "4%",
            width: "88%",
            height: "82%",
            backgroundImage: "url('/assets/images/shape.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />

        {/* Thumbnails — Left column */}
        <div
          className="absolute hero-thumb-left"
          style={{ left: "10%", top: "10%", width: "16%", "--delay-in": "0.9s", "--delay-float": "1.65s", "--float-dur": "5s" }}
        >
          <img
            src="/assets/thumbnails/thumb-mario.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(-4deg)" }}
          />
        </div>
        <div
          className="absolute hero-thumb-left"
          style={{ left: "15%", top: "34%", width: "12%", "--delay-in": "1.02s", "--delay-float": "2.05s", "--float-dur": "5.5s" }}
        >
          <img
            src="/assets/thumbnails/thumb-golf-wheel.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(-2deg)" }}
          />
        </div>
        <div
          className="absolute hero-thumb-left"
          style={{ left: "12%", top: "56%", width: "13.5%", "--delay-in": "1.14s", "--delay-float": "1.90s", "--float-dur": "4.8s" }}
        >
          <img
            src="/assets/thumbnails/thumb-grocery.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(-3deg)" }}
          />
        </div>

        {/* Thumbnails — Right column */}
        <div
          className="absolute hero-thumb-right"
          style={{ right: "10%", top: "10%", width: "16%", "--delay-in": "1.0s", "--delay-float": "1.85s", "--float-dur": "5.2s" }}
        >
          <img
            src="/assets/thumbnails/thumb-netflix.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(4deg)" }}
          />
        </div>
        <div
          className="absolute hero-thumb-right"
          style={{ right: "13.5%", top: "34%", width: "12%", "--delay-in": "1.12s", "--delay-float": "2.15s", "--float-dur": "5.6s" }}
        >
          <img
            src="/assets/thumbnails/thumb-efoil.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(2deg)" }}
          />
        </div>
        <div
          className="absolute hero-thumb-right"
          style={{ right: "10.5%", top: "56%", width: "14%", "--delay-in": "1.24s", "--delay-float": "1.75s", "--float-dur": "4.6s" }}
        >
          <img
            src="/assets/thumbnails/thumb-gym-shed.jpg"
            alt=""
            aria-hidden="true"
            className="w-full rounded-[18px] object-cover block"
            style={{ ...thumbStyleBase, transform: "rotate(3deg)" }}
          />
        </div>

        {/* Centered headline block */}
        <div
          className="absolute inset-x-0 flex flex-col items-center text-center"
          style={{ top: "13%" }}
        >
          <h1
            className="hero-anim-yt"
            style={{
              fontFamily: "'Clash Display',sans-serif",
              fontWeight: 600,
              fontSize: "clamp(80px, 8.5vw, 162px)",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
              margin: 0,
            }}
          >
            YouTube
          </h1>

          <p
            className="hero-anim-sub"
            style={{
              fontFamily: "'Karvest','SF Pro Display',sans-serif",
              fontWeight: 400,
              fontSize: "clamp(24px, 2.35vw, 46px)",
              lineHeight: 1.1,
              letterSpacing: "0.01em",
              color: "#9AA3B7",
              margin: "2.2% 0 1.8% 0",
            }}
          >
            Packaging Built To
          </p>

          <div className="relative inline-flex items-center hero-anim-cta">
            <h2
              style={{
                fontFamily: "'Clash Display',sans-serif",
                fontWeight: 500,
                fontSize: "clamp(50px, 5.2vw, 100px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              Earn More Clicks
            </h2>
            <img
              src="/assets/images/click-icon.png"
              alt=""
              aria-hidden="true"
              className="absolute pointer-events-none hero-anim-curs"
              style={{
                right: "-4.5%",
                bottom: "-52%",
                width: "clamp(44px, 4vw, 78px)",
                height: "auto",
              }}
            />
          </div>
        </div>

        {/* Left pill button — View Work */}
        <button
          type="button"
          className="absolute inline-flex items-center justify-center gap-3 rounded-full text-white hero-anim-btn"
          style={{
            left: "28%",
            top: "78%",
            transform: "translateY(-50%)",
            padding: "1.15% 2.4%",
            border: "1px solid rgba(139,92,246,0.32)",
            backgroundColor: "rgba(20,22,47,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(17px, 1.25vw, 25px)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(calc(-50% - 2px))";
            e.currentTarget.style.boxShadow = "0 14px 32px rgba(79,70,229,0.40), inset 0 1px 0 rgba(255,255,255,0.12)";
            e.currentTarget.style.backgroundColor = "rgba(30,32,60,0.75)";
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.55)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(-50%)";
            e.currentTarget.style.boxShadow = "0 8px 22px rgba(0,0,0,0.35)";
            e.currentTarget.style.backgroundColor = "rgba(20,22,47,0.55)";
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.32)";
          }}
          onClick={() => {
            const el = document.querySelector("#featured");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B5CF6"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="7" y1="17" x2="17" y2="7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
          View Work
        </button>

        {/* Right pill anchor — Book a Project */}
        <a
          href="https://tally.so/r/b5EyVe"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute inline-flex items-center justify-center gap-3 rounded-full text-white hero-anim-btn"
          style={{
            right: "28%",
            top: "78%",
            transform: "translateY(-50%)",
            padding: "1.15% 2.4%",
            border: "1px solid rgba(139,92,246,0.32)",
            backgroundColor: "rgba(20,22,47,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(17px, 1.25vw, 25px)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(calc(-50% - 2px))";
            e.currentTarget.style.boxShadow = "0 14px 32px rgba(79,70,229,0.40), inset 0 1px 0 rgba(255,255,255,0.12)";
            e.currentTarget.style.backgroundColor = "rgba(30,32,60,0.75)";
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.55)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(-50%)";
            e.currentTarget.style.boxShadow = "0 8px 22px rgba(0,0,0,0.35)";
            e.currentTarget.style.backgroundColor = "rgba(20,22,47,0.55)";
            e.currentTarget.style.borderColor = "rgba(139,92,246,0.32)";
          }}
        >
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B5CF6"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="7" y1="17" x2="17" y2="7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
          Book a Project
        </a>

        {/* Center circular badge with Clickory logo */}
        <div
          className="absolute flex items-center justify-center rounded-full hero-anim-logo"
          style={{
            top: "78%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "9%",
            aspectRatio: "1/1",
            background: "linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)",
            boxShadow: "0 16px 60px rgba(79,70,229,0.55)",
          }}
        >
          <img
            src="/assets/brand/clickory-mark.png"
            alt="Clickory"
            data-testid="hero-logo-mark"
            style={{ width: "54%", height: "auto" }}
          />
        </div>
      </div>
    </section>
  );
}
